import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MessageMarkdown } from '@/components/ui/messages/message-markdown';
import { Upload, FileText, Image as ImageIcon, File } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.mjs?url';
import { useDemoMode } from '@/context/DemoModeContext';
import { useListSSHInstances } from '@/service/gpu-instance/hook';

// Configure PDF.js worker to use local module
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Mock OCR results for demo mode
const MOCK_OCR_RESULT = `# Thai ID Card - Extracted Text

## Personal Information

| Field | Value |
|-------|-------|
| **Name (Thai)** | นายสมชาย ใจดี |
| **Name (English)** | Mr. SOMCHAI JAIDEE |
| **Date of Birth** | 15 มกราคม 2533 |
| **ID Number** | 1-1001-12345-67-8 |
| **Address** | 123/45 ซอยสุขุมวิท 55 แขวงคลองตันเหนือ เขตวัฒนา กรุงเทพมหานคร 10110 |

## Additional Details

- **Issue Date:** 1 มกราคม 2565
- **Expiry Date:** 14 มกราคม 2573
- **Issuing Office:** สำนักงานเขตวัฒนา

---
*Document processed with Typhoon OCR v2.0*`;

const MOCK_NER_RESULT = `# Named Entity Recognition Results

## Extracted Entities

### Person Names
- **นายสมชาย ใจดี** (Thai Name)
- **Mr. SOMCHAI JAIDEE** (English Name)

### Numbers & IDs
- **1-1001-12345-67-8** (Thai National ID)

### Dates
- **15 มกราคม 2533** (Date of Birth)
- **1 มกราคม 2565** (Issue Date)
- **14 มกราคม 2573** (Expiry Date)

### Locations
- **123/45 ซอยสุขุมวิท 55** (Street Address)
- **แขวงคลองตันเหนือ** (Sub-district)
- **เขตวัฒนา** (District)
- **กรุงเทพมหานคร** (Province)
- **10110** (Postal Code)

### Organizations
- **สำนักงานเขตวัฒนา** (Government Office)

---
*Entities extracted using Typhoon NER v2.0*`;

const MOCK_CLASSIFICATION_RESULT = `Document Type: Thai National ID Card (บัตรประจำตัวประชาชน)

Confidence: 98.5%

Classification Details:
- Category: Government-issued Identity Document
- Sub-type: National Identification Card
- Country: Thailand
- Language: Thai / English (Bilingual)

Document Features Detected:
- [x] National emblem (Garuda)
- [x] Holographic strip
- [x] Photo ID section
- [x] Machine-readable zone (MRZ)
- [x] Thai national ID format (X-XXXX-XXXXX-XX-X)`;

const OCRPlaygroundPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { isDemoMode } = useDemoMode();
  const { data: instances } = useListSSHInstances();

  const instance = instances?.find((i) => i.task_id === instanceId);

  // OCR-specific state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [ocrResult, setOcrResult] = useState<string | null>(null);
  const [nerResult, setNerResult] = useState<string | null>(null);
  const [classificationResult, setClassificationResult] = useState<string | null>(null);
  const [showMarkdown, setShowMarkdown] = useState(true);

  // Convert PDF to image at high DPI
  const convertPdfToImage = useCallback(async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);

    // High DPI for better OCR quality
    const DPI = 600;
    const scale = DPI / 72;
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Failed to get canvas context');

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({
      canvasContext: context,
      viewport: viewport,
    }).promise;

    return canvas.toDataURL('image/png');
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PNG, JPG, JPEG or PDF file');
      return;
    }

    setUploadedFile(file);
    setOcrResult(null);
    setNerResult(null);
    setClassificationResult(null);

    // Generate preview
    if (file.type === 'application/pdf') {
      try {
        const imageUrl = await convertPdfToImage(file);
        setFilePreviewUrl(imageUrl);
      } catch (error) {
        console.error('Error converting PDF:', error);
        setFilePreviewUrl(null);
      }
    } else {
      // For images, create object URL
      const url = URL.createObjectURL(file);
      setFilePreviewUrl(url);
    }
  }, [convertPdfToImage]);

  // Handle OCR processing
  const handleProcessOCR = useCallback(async () => {
    if (!uploadedFile) return;

    setIsProcessingOCR(true);

    try {
      // In demo mode, simulate processing with mock results
      if (isDemoMode) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        setOcrResult(MOCK_OCR_RESULT);
        setNerResult(MOCK_NER_RESULT);
        setClassificationResult(MOCK_CLASSIFICATION_RESULT);
      } else {
        // TODO: Implement actual OCR API call for non-demo mode
        // For now, show a message that the feature requires the OCR service
        setOcrResult('OCR processing requires a running OCR service endpoint. Please ensure the instance is properly configured.');
        setNerResult('NER processing requires a running OCR service endpoint.');
        setClassificationResult('Document classification requires a running OCR service endpoint.');
      }
    } catch (error) {
      console.error('Error processing OCR:', error);
      setOcrResult('Error processing document. Please try again.');
    } finally {
      setIsProcessingOCR(false);
    }
  }, [uploadedFile, isDemoMode]);

  // Copy to clipboard helper
  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
  }, []);

  return (
    <div className="container mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">OCR Playground</h1>
          <p className="text-sm text-gray-500">
            Extract text from Thai documents using Typhoon OCR
          </p>
        </div>
        {instance && (
          <div className="text-right text-sm text-gray-500">
            <div>Instance: {instance.task_name}</div>
            <div>Status: {instance.status}</div>
          </div>
        )}
      </div>

      {/* Main OCR Interface */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left Column: Upload Area */}
        <div className="space-y-6">
          <div className="rounded-lg border bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold">Upload Document</h2>

            {/* File Upload Area */}
            <div className="space-y-4">
              <label
                htmlFor="file-upload"
                className="flex min-h-[200px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="space-y-2 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="text-sm text-gray-600">
                    <span className="font-semibold text-primary">Click to upload</span> or drag and drop
                  </div>
                  <p className="text-xs text-gray-500">
                    PNG, JPG, JPEG or PDF (max 10MB)
                  </p>
                </div>
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  accept="image/png,image/jpeg,image/jpg,application/pdf"
                  onChange={handleFileUpload}
                />
              </label>

              {/* File Preview */}
              {uploadedFile && (
                <div className="rounded-lg border bg-white p-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      {uploadedFile.type.startsWith('image/') ? (
                        <ImageIcon className="h-6 w-6 text-primary" />
                      ) : (
                        <File className="h-6 w-6 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {uploadedFile.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(uploadedFile.size / 1024).toFixed(2)} KB
                        {uploadedFile.type === 'application/pdf' && ' - Converted to 600 DPI'}
                      </p>
                    </div>
                  </div>

                  {/* Image Preview */}
                  {filePreviewUrl && (
                    <div className="mt-4">
                      <img
                        src={filePreviewUrl}
                        alt="Preview"
                        className="max-h-[300px] w-full rounded-lg object-contain"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Process Button */}
              <Button
                onClick={handleProcessOCR}
                disabled={!uploadedFile || isProcessingOCR}
                className="w-full"
                variant="primary"
              >
                {isProcessingOCR ? 'Processing...' : 'Extract Text'}
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column: Results */}
        <div className="space-y-6">
          <div className="rounded-lg border bg-white shadow-sm">
            <Tabs defaultValue="ocr" className="w-full">
              <TabsList variant={'outline'} className="w-full">
                <TabsTrigger value="ocr" variant={'outline'} className="flex-1">
                  OCR Result
                </TabsTrigger>
                <TabsTrigger value="ner" variant={'outline'} className="flex-1">
                  Named Entities
                </TabsTrigger>
                <TabsTrigger value="classification" variant={'outline'} className="flex-1">
                  Document Type
                </TabsTrigger>
              </TabsList>

              {/* OCR Tab */}
              <TabsContent value="ocr" className="p-6">
                {ocrResult ? (
                  <div className="space-y-4">
                    {/* Toggle between Markdown and Raw Text */}
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setShowMarkdown(true)}
                          variant={showMarkdown ? 'primary' : 'outline'}
                          size="sm"
                        >
                          Markdown
                        </Button>
                        <Button
                          onClick={() => setShowMarkdown(false)}
                          variant={!showMarkdown ? 'primary' : 'outline'}
                          size="sm"
                        >
                          Raw Text
                        </Button>
                      </div>
                      <Button
                        onClick={() => copyToClipboard(ocrResult)}
                        variant="outline"
                        size="sm"
                        startIcon={<FileText size={16} />}
                      >
                        Copy to Clipboard
                      </Button>
                    </div>

                    {/* Content Display */}
                    <div className="rounded-lg bg-gray-50 p-4">
                      {showMarkdown ? (
                        <MessageMarkdown content={ocrResult} />
                      ) : (
                        <pre className="whitespace-pre-wrap text-sm text-gray-900">
                          {ocrResult}
                        </pre>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex min-h-[300px] items-center justify-center text-center">
                    <div className="space-y-2">
                      <FileText className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="text-sm text-gray-500">
                        {isProcessingOCR
                          ? 'Extracting text from document...'
                          : 'Upload and process a document to see extracted text'}
                      </p>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* NER Tab */}
              <TabsContent value="ner" className="p-6">
                {nerResult ? (
                  <div className="space-y-4">
                    {/* Toggle between Markdown and Raw Text */}
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setShowMarkdown(true)}
                          variant={showMarkdown ? 'primary' : 'outline'}
                          size="sm"
                        >
                          Markdown
                        </Button>
                        <Button
                          onClick={() => setShowMarkdown(false)}
                          variant={!showMarkdown ? 'primary' : 'outline'}
                          size="sm"
                        >
                          Raw Text
                        </Button>
                      </div>
                      <Button
                        onClick={() => copyToClipboard(nerResult)}
                        variant="outline"
                        size="sm"
                        startIcon={<FileText size={16} />}
                      >
                        Copy to Clipboard
                      </Button>
                    </div>

                    {/* Content Display */}
                    <div className="rounded-lg bg-gray-50 p-4">
                      {showMarkdown ? (
                        <MessageMarkdown content={nerResult} />
                      ) : (
                        <pre className="whitespace-pre-wrap text-sm text-gray-900">
                          {nerResult}
                        </pre>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex min-h-[300px] items-center justify-center text-center">
                    <div className="space-y-2">
                      <FileText className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="text-sm text-gray-500">
                        {isProcessingOCR
                          ? 'Identifying named entities...'
                          : 'Upload and process a document to see named entities'}
                      </p>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Classification Tab */}
              <TabsContent value="classification" className="p-6">
                {classificationResult ? (
                  <div className="space-y-4">
                    <div className="rounded-lg bg-gray-50 p-4">
                      <pre className="whitespace-pre-wrap text-sm text-gray-900">
                        {classificationResult}
                      </pre>
                    </div>
                    <Button
                      onClick={() => copyToClipboard(classificationResult)}
                      variant="outline"
                      size="sm"
                      startIcon={<FileText size={16} />}
                    >
                      Copy to Clipboard
                    </Button>
                  </div>
                ) : (
                  <div className="flex min-h-[300px] items-center justify-center text-center">
                    <div className="space-y-2">
                      <FileText className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="text-sm text-gray-500">
                        {isProcessingOCR
                          ? 'Classifying document type...'
                          : 'Upload and process a document to see document type'}
                      </p>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div className="rounded-lg border bg-white p-6">
        <h3 className="mb-4 text-lg font-semibold">About Thai Document OCR</h3>
        <p className="text-sm text-gray-600 mb-4">
          This OCR playground uses Typhoon OCR, a specialized model for extracting text from Thai documents
          including ID cards, passports, driver's licenses, and other official documents. The model provides:
        </p>
        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
          <li>High-accuracy Thai and English text extraction</li>
          <li>Named Entity Recognition (NER) for identifying key information</li>
          <li>Document type classification</li>
          <li>Support for PDF and image formats (PNG, JPG, JPEG)</li>
          <li>High DPI processing for better accuracy (600 DPI for PDFs)</li>
        </ul>
        <div className="flex flex-wrap gap-2 mt-4">
          {['OCR', 'Thai', 'NER', 'Document Classification', 'PDF', 'Image Processing'].map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OCRPlaygroundPage;
